import type { FieldMergeFunction, Reference, StoreObject, TypePolicies } from '@apollo/client';

/**
 * Apollo cache field policies for apps/web.
 *
 * `Query.endUserTimeline` (Users-surface redesign B4) is keyset-paginated: the
 * first page is `{ sub, environment }` and "Load more" refetches with an added
 * `cursor`. We key the cache entry by `sub` + `environment` ONLY (not `cursor`),
 * so every page of one user's feed lands in the same cache slot, and merge:
 *
 * - **First page** (`cursor` absent) REPLACES — a `cache-and-network` re-poll of
 *   the top of the feed must not append a duplicate copy of itself.
 * - **Subsequent pages** (`cursor` present) APPEND older events, de-duplicated by
 *   the event's synthetic `id` (defensive against an overlap between a re-polled
 *   first page and an already-appended tail).
 *
 * `nextCursor` always takes the incoming page's value — it is the cursor to
 * continue FROM, so the freshest page is authoritative.
 */

/** The cached page shape — events are normalized to references by the cache. */
interface CachedTimelinePage {
  events: readonly (Reference | StoreObject)[];
  nextCursor: string | null;
}

const mergeEndUserTimeline: FieldMergeFunction<
  CachedTimelinePage | undefined,
  CachedTimelinePage
> = (existing, incoming, { args, readField }) => {
  const isFirstPage = args?.cursor == null;
  const incomingEvents = incoming.events;
  const existingEvents = isFirstPage ? [] : (existing?.events ?? []);

  const seen = new Set<unknown>();
  const merged: (Reference | StoreObject)[] = [];
  for (const event of [...existingEvents, ...incomingEvents]) {
    const id = readField('id', event);
    if (id !== undefined && seen.has(id)) continue;
    if (id !== undefined) seen.add(id);
    merged.push(event);
  }

  return { ...incoming, events: merged, nextCursor: incoming.nextCursor };
};

export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      endUserTimeline: {
        keyArgs: ['sub', 'environment'],
        merge: mergeEndUserTimeline,
      },
    },
  },
  /**
   * A singleton report with no `id`, read by two cards with DISJOINT selection
   * sets: `TopCapabilitiesCard` takes `annotations`, `RuntimeDiscoveryCard`
   * takes `shadowEndpoints` + `shapeProposals`.
   *
   * Apollo's default for a non-normalized object is REPLACE, so whichever card
   * resolved last evicted the other's fields (logging "Cache data may be lost
   * when replacing the catalogReconciliation field") and its sibling re-rendered
   * empty. `merge: true` shallow-merges the two field sets into one cache entry
   * instead — correct here because both cards read the same singleton report for
   * the same `environment`, so the field sets are complementary, never rival.
   *
   * The two cards used to mount TOGETHER on `/home`; § 2.4 moved
   * `TopCapabilitiesCard` to the Analytics hub's Usage room, so they are now on
   * different routes. That does NOT retire this policy — the cache outlives a
   * route change, so the hazard simply moved with them: navigating `/home` →
   * `/analytics?room=usage` writes `annotations` over an entry holding
   * `shadowEndpoints`, and navigating back renders the discovery card empty off
   * the cache until the network answers. A hazard that is sequential rather than
   * simultaneous is harder to notice, not smaller.
   */
  CatalogReconciliationReport: {
    merge: true,
  },
  /**
   * The same shape as `CatalogReconciliationReport` above, one surface along, and
   * proved rather than inferred (`site-walk/coverage-collision.test.ts`).
   *
   * `Query.discoveryCoverage` is a keyless singleton read by THREE queries with
   * disjoint selection sets — `SiteWalkPublishedRoutes` takes
   * `publishedRouteCoverage` (PA-20's not-reached line), `SiteWalkRouteAttestation`
   * takes `routeAttestation` (WF-4.39's line, on the SAME report), and
   * `SiteWalkRoleCoverage` takes `audienceCoverage` (the role matrix). Apollo's
   * default for an unnormalized object is REPLACE, so whichever resolved last
   * evicted the others: after two writes, `readQuery` for the loser returns
   * `null`.
   *
   * **A component test cannot see it**, which is why it lasted. Each `useQuery`
   * holds its own delivered result, so a rendered pair passes; the loss shows on
   * the next cache READ — a remount, a `cache-first` read, a broadcast — and
   * `useWalkReportFacts` is `cache-and-network`, so re-opening the report within a
   * session is exactly that read. The proof asserts on the CACHE for that reason.
   *
   * Correct here for the reason the entry above gives: all three read the same
   * singleton for the same `environment`, so the field sets are complementary
   * rather than rival.
   */
  DiscoveryCoverage: {
    merge: true,
  },
  /**
   * D2 — the SAME defect as `DiscoveryCoverage` above, added in the same change
   * as the second reader rather than after a cache read returns `null`.
   *
   * `WalkApprovalQueue` has no `id`, and two documents now read disjoint field
   * sets from it: C3b's screen reads the whole queue with every tool on it, and
   * the Map Report reads `awaitingDecision` alone to decide whether to offer the
   * approval screen at all. Apollo's default for an unnormalized object is
   * REPLACE, so whichever resolved last would EVICT the other's fields — and the
   * report's read is `cache-and-network`, which is exactly the read that then
   * returns `null`.
   *
   * Written here on the first day there were two readers, because the entry above
   * records what it costs to find this later: `DiscoveryCoverage` had been
   * colliding across three queries and was found by `CONSOLE_GATE=strict` rather
   * than by anybody noticing a missing line.
   */
  WalkApprovalQueue: {
    merge: true,
  },
  /**
   * W5b — a per-workspace `/monitoring` target has no `id`; its identity IS the
   * metric it names (one row per (tenant, metric)). Keying on `metric` lets the
   * set/clear mutations' returned object update the `monitoringTargets` list in
   * place, so a tile shows its new target without a refetch — and stops three
   * separately-fetched targets collapsing into one unkeyed cache entry.
   */
  MonitoringTarget: {
    keyFields: ['metric'],
  },
};
